import React, { useEffect } from "react";
import propTypes from "prop-types";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { SimpleErrorDisplay } from "@qgiv/core-react";
import {
    composeMultiplePaths,
    initGoogleAnalytics,
    initMatomoAnalyticsTracking,
    submitErrorData,
    getSessionStorageKey,
    asyncSessionStorage,
} from "@qgiv/core-donor";
import {
    ConnectedClassificationPage,
    ReceiptPage,
    RegistrationClosedPage,
    ConnectedAccountPage,
    ConnectedRegistrationPage,
    ConnectedNavigateToBaseUrl,
    ConnectedPreselectTeam,
    ConnectedPreselectClassification,
    ConnectedPaymentPage,
    ConnectedStorePage,
} from "./Pages";
import ConnectedUpdateOutdated from "./ConnectedUpdateOutdated";
import ConnectedUpdateRegistrationTotal from "./ConnectedUpdateRegistrationTotal";
import ConnectedUpdateEarlySubmission from "./ConnectedUpdateEarlySubmission";
import ConnectedUpdateNavigation from "./ConnectedUpdateNavigation";
import ConnectedUpdateEventDataFromSessionStorage from "./ConnectedUpdateEventDataFromSessionStorage";
import AnalyticsEvents from "./AnalyticsEvents";

// Every route will load these components
const RootEntryComponent = () => (
    <>
        <AnalyticsEvents />
        <ConnectedUpdateNavigation />
        <Outlet />
    </>
);

// Every /form route will load these components
const RootFormComponent = () => (
    <>
        <ConnectedUpdateOutdated />
        <ConnectedUpdateRegistrationTotal />
        <ConnectedUpdateEarlySubmission />
        <ConnectedUpdateEventDataFromSessionStorage />
        <Outlet />
    </>
);

const Routes = ({ formId, formType, orgId, title, baseUrl }) => {
    const matomo = useMatomo();

    // Load Google Analytics & Matomo Analytics
    useEffect(() => {
        const options = {
            formId,
            formType,
            orgId,
            title,
        };
        initGoogleAnalytics(options);
        initMatomoAnalyticsTracking(matomo, options);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sessionStorageKey = getSessionStorageKey(formId);

    /**
     * @function removeSessionStorage
     * @description function that runs asyncSessionStorage.remove()
     */
    function removeSessionStorage() {
        asyncSessionStorage.remove(sessionStorageKey);
    }

    // form routes with child page routes
    const formRoutePaths = composeMultiplePaths({
        paths: ["form"],
        element: <RootFormComponent />,
        // form child routes
        children: [
            {
                path: "classification",
                element: <ConnectedClassificationPage />,
            },
            {
                path: "registration",
                element: <ConnectedRegistrationPage />,
            },
            {
                path: "store",
                element: <ConnectedStorePage />,
            },
            {
                path: "payment",
                element: <ConnectedPaymentPage />,
            },
            {
                path: "closed",
                element: <RegistrationClosedPage />,
            },
            // Redirect parent registration url to base url
            // Data for parent registration is handled in redux
            {
                path: "parentregistration/:registrationId",
                element: <ConnectedNavigateToBaseUrl />,
            },
        ],
    });

    // Need to load account routes outside of root component base
    // original comment from AccountRoutes below:

    // Add a dynamic registration id and unique identifier section to all of
    // the account routes so that we can match the unique urls created by the
    // backend for these views. The unique urls are required as we presently
    // load and display PII such as an email address on all of these pages.
    const accountRoutePaths = composeMultiplePaths({
        paths: [
            `${baseUrl}/account/:registrationId/resetpassword/:uniqueIdentifier`,
            `${baseUrl}/account/:registrationId/:uniqueIdentifier`,
        ],
        element: <ConnectedAccountPage />,
    });

    const router = createBrowserRouter([
        {
            path: baseUrl,
            errorElement: (
                <SimpleErrorDisplay
                    submitErrorData={submitErrorData}
                    // eslint-disable-next-line react/jsx-no-bind
                    removeSessionStorage={removeSessionStorage}
                />
            ),
            element: <RootEntryComponent />,
            children: [
                ...formRoutePaths,
                { path: ":receiptId/receipt", element: <ReceiptPage /> },
                {
                    path: "classification/:classificationId",
                    element: <ConnectedPreselectClassification />,
                },
                {
                    path: "team/:teamId",
                    element: <ConnectedPreselectTeam />,
                },
            ],
        },
        ...accountRoutePaths,
        // catch all 404 route, mostly handled by BE redirects
        {
            path: "*",
            element: <ConnectedNavigateToBaseUrl />,
        },
    ]);

    return <RouterProvider router={router} />;
};

Routes.propTypes = {
    baseUrl: propTypes.string.isRequired,
    formId: propTypes.number.isRequired,
    orgId: propTypes.number.isRequired,
    title: propTypes.string.isRequired,
    formType: propTypes.number.isRequired,
};

export default Routes;
